import { useEffect, useState } from "react";
import { X, Download, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { canInstall, isAndroid, isIos, isStandalone, subscribeInstallable, triggerInstallPrompt } from "@/lib/pwa";

export default function InstallPrompt({ open, onClose }) {
  const [state, setState] = useState({ canInstall: canInstall(), installed: isStandalone() });

  useEffect(() => {
    if (!open) return undefined;
    return subscribeInstallable(setState);
  }, [open]);

  if (!open) return null;

  const install = async () => {
    const res = await triggerInstallPrompt();
    if (res.outcome === "accepted") {
      toast.success("Workspace is being installed.");
      onClose?.();
    } else if (res.outcome === "unavailable") {
      toast.message("Your browser will prompt you when it's ready.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm p-3 sm:items-center" data-testid="install-prompt">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md brand-gradient-bg text-white">
              <Smartphone className="h-4 w-4" />
            </span>
            <p className="font-display text-sm font-semibold">Install Workspace</p>
          </div>
          <button onClick={onClose} data-testid="install-prompt-close" className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5">
          {state.installed ? (
            <p className="text-sm text-muted-foreground" data-testid="install-status-installed">
              Workspace is already installed on this device. Look for the app icon on your home screen.
            </p>
          ) : isIos() ? (
            <div className="space-y-3 text-sm" data-testid="install-guide-ios">
              <p>On iPhone or iPad, add Workspace to your Home Screen — Push Notifications need this step first.</p>
              <ol className="ml-5 list-decimal space-y-1.5 text-muted-foreground">
                <li>Tap the <b>Share</b> icon in Safari's bottom toolbar.</li>
                <li>Choose <b>Add to Home Screen</b>.</li>
                <li>Confirm the name <b>Workspace</b> and tap <b>Add</b>.</li>
                <li>Open Workspace from the new Home Screen icon.</li>
              </ol>
            </div>
          ) : state.canInstall ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Get Workspace as its own app — instant open, its own icon, and push reminders.</p>
              <button onClick={install} data-testid="install-prompt-install-btn" className="inline-flex w-full items-center justify-center gap-2 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white">
                <Download className="h-4 w-4" /> Install Workspace
              </button>
            </div>
          ) : (
            <div className="space-y-3 text-sm" data-testid="install-guide-android">
              <p>Open the browser menu to install Workspace as an app.</p>
              <ol className="ml-5 list-decimal space-y-1.5 text-muted-foreground">
                <li>Tap the browser menu ({isAndroid() ? "⋮ on Chrome" : "your browser's menu"}).</li>
                <li>Choose <b>Install app</b> or <b>Add to Home Screen</b>.</li>
                <li>Confirm to add Workspace as its own icon.</li>
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
